/**
 * VariantLab.jsx
 *
 * A/B Test Variant Generation Component
 * Allows users to generate and manage A/B test variants for their creatives
 */

import React, { useState } from 'react';
import {
  Beaker,
  Type,
  Image,
  MousePointer,
  FileText,
  Compass,
  Layers,
  Loader2,
  ChevronRight,
  Check,
  AlertCircle,
  Plus,
  X,
  Sparkles,
  BarChart3
} from 'lucide-react';
import { AB_TEST_ELEMENTS } from '../services/conversationAgentV2';

// Icon mapping for test elements
const ELEMENT_ICONS = {
  headline: Type,
  visual: Image,
  cta: MousePointer,
  copy: FileText,
  angle: Compass,
  multiple: Layers
};

/**
 * VariantLab Component
 * @param {Object} props
 * @param {Object} props.creativeBrief - The base creative brief
 * @param {Object} props.conversationAgent - The conversation agent instance
 * @param {Function} props.onVariantsGenerated - Callback when variants are generated
 * @param {Function} props.onClose - Callback to close the panel
 * @param {Array} props.existingVariants - Any existing variants
 */
const VariantLab = ({
  creativeBrief,
  conversationAgent,
  onVariantsGenerated,
  onClose,
  existingVariants = []
}) => {
  // State
  const [step, setStep] = useState('select'); // 'select' | 'configure' | 'generating' | 'review'
  const [selectedElement, setSelectedElement] = useState(null);
  const [variantCount, setVariantCount] = useState(2);
  const [hypothesis, setHypothesis] = useState('');
  const [generatedVariants, setGeneratedVariants] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestions, setSuggestions] = useState(null);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);

  // Load AI suggestions for what to test
  const loadSuggestions = async () => {
    if (!conversationAgent || suggestions) return;

    setIsLoadingSuggestions(true);
    try {
      const result = await conversationAgent.suggestABTestOpportunities(creativeBrief);
      setSuggestions(result);
    } catch (err) {
      console.error('Failed to load suggestions:', err);
    } finally {
      setIsLoadingSuggestions(false);
    }
  };

  // Generate variants
  const handleGenerateVariants = async () => {
    if (!selectedElement || !conversationAgent) return;

    setStep('generating');
    setIsLoading(true);
    setError(null);

    try {
      const result = await conversationAgent.generateABVariants(
        creativeBrief,
        selectedElement,
        variantCount,
        hypothesis
      );

      setGeneratedVariants(result);
      setStep('review');
    } catch (err) {
      console.error('Failed to generate variants:', err);
      setError(err.message || 'Failed to generate variants');
      setStep('configure');
    } finally {
      setIsLoading(false);
    }
  };

  // Accept variants and pass to parent
  const handleAcceptVariants = () => {
    if (generatedVariants && onVariantsGenerated) {
      onVariantsGenerated({
        ...generatedVariants,
        baseBrief: creativeBrief
      });
    }
    onClose?.();
  };

  // Render test element selection
  const renderElementSelection = () => (
    <div className="variant-lab-section">
      <h3>What do you want to test?</h3>
      <p className="variant-lab-description">
        Select the element you want to create variants for. We'll generate alternatives
        while keeping other elements consistent.
      </p>

      <div className="variant-lab-elements">
        {Object.entries(AB_TEST_ELEMENTS).map(([key, element]) => {
          const Icon = ELEMENT_ICONS[key] || Beaker;
          const isSelected = selectedElement === key;
          const suggestion = suggestions?.opportunities?.find(o => o.testElement === key);

          return (
            <button
              key={key}
              className={`variant-element-card ${isSelected ? 'selected' : ''}`}
              onClick={() => setSelectedElement(key)}
            >
              <div className="element-icon">
                <Icon size={24} />
              </div>
              <div className="element-info">
                <div className="element-label">{element.label}</div>
                <div className="element-description">{element.description}</div>
              </div>
              {suggestion && (
                <div className={`element-impact impact-${suggestion.potentialImpact}`}>
                  {suggestion.potentialImpact} impact
                </div>
              )}
              {isSelected && (
                <div className="element-check">
                  <Check size={16} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* AI Suggestions */}
      {!suggestions && !isLoadingSuggestions && (
        <button
          className="variant-lab-suggest-btn"
          onClick={loadSuggestions}
        >
          <Sparkles size={16} />
          Get AI Recommendations
        </button>
      )}

      {isLoadingSuggestions && (
        <div className="variant-lab-loading-suggestions">
          <Loader2 size={16} className="animate-spin" />
          Analyzing your creative...
        </div>
      )}

      {suggestions?.recommendation && (
        <div className="variant-lab-recommendation">
          <BarChart3 size={16} />
          <span>{suggestions.recommendation}</span>
        </div>
      )}

      <div className="variant-lab-actions">
        <button className="btn btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={() => setStep('configure')}
          disabled={!selectedElement}
        >
          Continue
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );

  // Render configuration step
  const renderConfiguration = () => (
    <div className="variant-lab-section">
      <h3>Configure Your Test</h3>

      <div className="variant-lab-selected">
        <span className="selected-label">Testing:</span>
        <span className="selected-value">{AB_TEST_ELEMENTS[selectedElement]?.label}</span>
        <button className="change-btn" onClick={() => setStep('select')}>
          Change
        </button>
      </div>

      <div className="variant-lab-config">
        {/* Variant Count */}
        <div className="config-field">
          <label>Number of Variants</label>
          <div className="variant-count-selector">
            {[2, 3, 4].map(count => (
              <button
                key={count}
                className={`count-btn ${variantCount === count ? 'selected' : ''}`}
                onClick={() => setVariantCount(count)}
              >
                {count} Variants
                <span className="count-labels">
                  {count === 2 ? '(A/B)' : count === 3 ? '(A/B/C)' : '(A/B/C/D)'}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Hypothesis */}
        <div className="config-field">
          <label>
            Your Hypothesis (Optional)
            <span className="field-hint">What do you think might work better?</span>
          </label>
          <textarea
            value={hypothesis}
            onChange={(e) => setHypothesis(e.target.value)}
            placeholder="e.g., I think a more urgent headline will increase CTR because our audience responds well to time-sensitive offers..."
            rows={3}
          />
        </div>
      </div>

      {error && (
        <div className="variant-lab-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div className="variant-lab-actions">
        <button className="btn btn-secondary" onClick={() => setStep('select')}>
          Back
        </button>
        <button
          className="btn btn-primary"
          onClick={handleGenerateVariants}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 size={16} className="animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Sparkles size={16} />
              Generate Variants
            </>
          )}
        </button>
      </div>
    </div>
  );

  // Render generating state
  const renderGenerating = () => (
    <div className="variant-lab-section variant-lab-generating">
      <div className="generating-animation">
        <Beaker size={48} className="animate-pulse" />
      </div>
      <h3>Generating Variants...</h3>
      <p>
        Our AI is creating {variantCount} distinct variants for your{' '}
        {AB_TEST_ELEMENTS[selectedElement]?.label.toLowerCase()} test.
      </p>
      <div className="generating-steps">
        <div className="step active">
          <Check size={14} />
          Analyzing your creative brief
        </div>
        <div className="step active">
          <Loader2 size={14} className="animate-spin" />
          Generating meaningful alternatives
        </div>
        <div className="step">
          Building test hypotheses
        </div>
      </div>
    </div>
  );

  // Render review step
  const renderReview = () => (
    <div className="variant-lab-section">
      <h3>Review Your Variants</h3>
      <p className="variant-lab-description">
        {generatedVariants?.testDescription}
      </p>

      <div className="variants-grid">
        {generatedVariants?.variants?.map((variant, idx) => (
          <div
            key={variant.id}
            className={`variant-card ${variant.isControl ? 'control' : ''}`}
          >
            <div className="variant-header">
              <span className="variant-id">Variant {variant.id}</span>
              {variant.isControl && <span className="control-badge">Control</span>}
            </div>
            <div className="variant-label">{variant.label}</div>
            <div className="variant-hypothesis">{variant.hypothesis}</div>

            <div className="variant-changes">
              <div className="changes-title">Changes:</div>
              {Object.entries(variant.changes || {}).map(([key, value]) => (
                <div key={key} className="change-item">
                  <span className="change-key">{key}:</span>
                  <span className="change-value">{value}</span>
                </div>
              ))}
            </div>

            <div className="variant-expected">
              <span className="expected-label">Expected:</span>
              {variant.expectedOutcome}
            </div>
          </div>
        ))}
      </div>

      {/* Test Configuration Summary */}
      <div className="test-config-summary">
        <div className="config-item">
          <span className="config-label">Recommended Metrics:</span>
          <span className="config-value">
            {generatedVariants?.recommendedMetrics?.join(', ')}
          </span>
        </div>
        <div className="config-item">
          <span className="config-label">Min Sample Size:</span>
          <span className="config-value">
            {generatedVariants?.minimumSampleSize?.toLocaleString()} impressions
          </span>
        </div>
        <div className="config-item">
          <span className="config-label">Recommended Duration:</span>
          <span className="config-value">
            {generatedVariants?.recommendedDuration}
          </span>
        </div>
      </div>

      <div className="variant-lab-actions">
        <button className="btn btn-secondary" onClick={() => setStep('configure')}>
          Regenerate
        </button>
        <button className="btn btn-primary" onClick={handleAcceptVariants}>
          <Check size={16} />
          Use These Variants
        </button>
      </div>
    </div>
  );

  return (
    <div className="variant-lab">
      <div className="variant-lab-header">
        <div className="variant-lab-icon">
          <Beaker size={24} />
        </div>
        <div>
          <h2>Variant Lab</h2>
          <p>Create A/B test variants for your creative</p>
        </div>
        <button className="variant-lab-close" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      <div className="variant-lab-content">
        {step === 'select' && renderElementSelection()}
        {step === 'configure' && renderConfiguration()}
        {step === 'generating' && renderGenerating()}
        {step === 'review' && renderReview()}
      </div>

      <style>{`
        .variant-lab {
          background: var(--color-bg-primary);
          border-radius: var(--radius-xl);
          border: 1px solid var(--color-border);
          overflow: hidden;
          max-width: 800px;
          margin: 0 auto;
        }

        .variant-lab-header {
          display: flex;
          align-items: center;
          gap: var(--space-4);
          padding: var(--space-5);
          background: linear-gradient(135deg, var(--color-primary-light) 0%, var(--color-bg-secondary) 100%);
          border-bottom: 1px solid var(--color-border-light);
        }

        .variant-lab-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-lg);
          background: var(--color-primary);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .variant-lab-header h2 {
          margin: 0;
          font-size: var(--font-size-xl);
          color: var(--color-text-primary);
        }

        .variant-lab-header p {
          margin: 0;
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
        }

        .variant-lab-close {
          margin-left: auto;
          background: none;
          border: none;
          color: var(--color-text-tertiary);
          cursor: pointer;
          padding: var(--space-2);
          border-radius: var(--radius-md);
          transition: all var(--transition-fast);
        }

        .variant-lab-close:hover {
          background: var(--color-bg-tertiary);
          color: var(--color-text-primary);
        }

        .variant-lab-content {
          padding: var(--space-5);
        }

        .variant-lab-section h3 {
          margin: 0 0 var(--space-2) 0;
          font-size: var(--font-size-lg);
          color: var(--color-text-primary);
        }

        .variant-lab-description {
          color: var(--color-text-secondary);
          font-size: var(--font-size-sm);
          margin-bottom: var(--space-5);
        }

        .variant-lab-elements {
          display: flex;
          flex-direction: column;
          gap: var(--space-3);
          margin-bottom: var(--space-5);
        }

        .variant-element-card {
          display: flex;
          align-items: center;
          gap: var(--space-4);
          padding: var(--space-4);
          background: var(--color-bg-secondary);
          border: 2px solid var(--color-border);
          border-radius: var(--radius-lg);
          cursor: pointer;
          text-align: left;
          transition: all var(--transition-fast);
        }

        .variant-element-card:hover {
          border-color: var(--color-primary);
          background: var(--color-bg-tertiary);
        }

        .variant-element-card.selected {
          border-color: var(--color-primary);
          background: var(--color-primary-light);
        }

        .element-icon {
          width: 48px;
          height: 48px;
          border-radius: var(--radius-md);
          background: var(--color-bg-tertiary);
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-text-secondary);
        }

        .variant-element-card.selected .element-icon {
          background: var(--color-primary);
          color: white;
        }

        .element-info {
          flex: 1;
        }

        .element-label {
          font-weight: 600;
          color: var(--color-text-primary);
          margin-bottom: var(--space-1);
        }

        .element-description {
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
        }

        .element-impact {
          padding: var(--space-1) var(--space-2);
          border-radius: var(--radius-sm);
          font-size: var(--font-size-xs);
          font-weight: 500;
          text-transform: uppercase;
        }

        .element-impact.impact-high {
          background: var(--color-success-light);
          color: var(--color-success);
        }

        .element-impact.impact-medium {
          background: var(--color-warning-light);
          color: var(--color-warning);
        }

        .element-impact.impact-low {
          background: var(--color-bg-tertiary);
          color: var(--color-text-tertiary);
        }

        .element-check {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: var(--color-primary);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .variant-lab-suggest-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-2);
          width: 100%;
          padding: var(--space-3);
          background: var(--color-bg-secondary);
          border: 1px dashed var(--color-border);
          border-radius: var(--radius-md);
          color: var(--color-primary);
          font-size: var(--font-size-sm);
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .variant-lab-suggest-btn:hover {
          background: var(--color-primary-light);
          border-color: var(--color-primary);
        }

        .variant-lab-loading-suggestions {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: var(--space-2);
          padding: var(--space-3);
          color: var(--color-text-secondary);
          font-size: var(--font-size-sm);
        }

        .variant-lab-recommendation {
          display: flex;
          align-items: flex-start;
          gap: var(--space-3);
          padding: var(--space-4);
          background: var(--color-primary-light);
          border-radius: var(--radius-md);
          color: var(--color-text-primary);
          font-size: var(--font-size-sm);
          margin-top: var(--space-4);
        }

        .variant-lab-recommendation svg {
          color: var(--color-primary);
          flex-shrink: 0;
          margin-top: 2px;
        }

        .variant-lab-actions {
          display: flex;
          justify-content: flex-end;
          gap: var(--space-3);
          margin-top: var(--space-5);
          padding-top: var(--space-4);
          border-top: 1px solid var(--color-border-light);
        }

        .variant-lab-selected {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-3);
          background: var(--color-bg-secondary);
          border-radius: var(--radius-md);
          margin-bottom: var(--space-5);
        }

        .selected-label {
          color: var(--color-text-secondary);
          font-size: var(--font-size-sm);
        }

        .selected-value {
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .change-btn {
          margin-left: auto;
          background: none;
          border: none;
          color: var(--color-primary);
          font-size: var(--font-size-sm);
          cursor: pointer;
        }

        .change-btn:hover {
          text-decoration: underline;
        }

        .variant-lab-config {
          display: flex;
          flex-direction: column;
          gap: var(--space-5);
        }

        .config-field label {
          display: block;
          font-weight: 600;
          color: var(--color-text-primary);
          margin-bottom: var(--space-2);
        }

        .field-hint {
          display: block;
          font-weight: 400;
          font-size: var(--font-size-sm);
          color: var(--color-text-tertiary);
          margin-top: var(--space-1);
        }

        .variant-count-selector {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-3);
        }

        .count-btn {
          display: flex;
          flex-direction: column;
          align-items: center;
          padding: var(--space-3);
          background: var(--color-bg-secondary);
          border: 2px solid var(--color-border);
          border-radius: var(--radius-md);
          cursor: pointer;
          font-weight: 600;
          color: var(--color-text-primary);
          transition: all var(--transition-fast);
        }

        .count-btn:hover {
          border-color: var(--color-primary);
        }

        .count-btn.selected {
          border-color: var(--color-primary);
          background: var(--color-primary-light);
        }

        .count-labels {
          font-size: var(--font-size-xs);
          font-weight: 400;
          color: var(--color-text-secondary);
          margin-top: var(--space-1);
        }

        .config-field textarea {
          width: 100%;
          padding: var(--space-3);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          resize: vertical;
          min-height: 80px;
          background: var(--color-bg-secondary);
          color: var(--color-text-primary);
        }

        .config-field textarea:focus {
          outline: none;
          border-color: var(--color-primary);
        }

        .variant-lab-error {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-3);
          background: var(--color-error-light);
          color: var(--color-error);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          margin-top: var(--space-4);
        }

        .variant-lab-generating {
          text-align: center;
          padding: var(--space-8) var(--space-5);
        }

        .generating-animation {
          width: 80px;
          height: 80px;
          margin: 0 auto var(--space-5);
          background: var(--color-primary-light);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--color-primary);
        }

        .generating-steps {
          display: flex;
          flex-direction: column;
          gap: var(--space-2);
          margin-top: var(--space-5);
          text-align: left;
          max-width: 300px;
          margin-left: auto;
          margin-right: auto;
        }

        .generating-steps .step {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          font-size: var(--font-size-sm);
          color: var(--color-text-tertiary);
        }

        .generating-steps .step.active {
          color: var(--color-text-primary);
        }

        .variants-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--space-4);
          margin-bottom: var(--space-5);
        }

        .variant-card {
          background: var(--color-bg-secondary);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-lg);
          padding: var(--space-4);
        }

        .variant-card.control {
          border-color: var(--color-primary);
        }

        .variant-header {
          display: flex;
          align-items: center;
          gap: var(--space-2);
          margin-bottom: var(--space-2);
        }

        .variant-id {
          font-weight: 700;
          font-size: var(--font-size-lg);
          color: var(--color-text-primary);
        }

        .control-badge {
          padding: 2px 8px;
          background: var(--color-primary);
          color: white;
          font-size: var(--font-size-xs);
          font-weight: 600;
          border-radius: var(--radius-sm);
        }

        .variant-label {
          font-weight: 600;
          color: var(--color-text-primary);
          margin-bottom: var(--space-2);
        }

        .variant-hypothesis {
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
          margin-bottom: var(--space-3);
        }

        .variant-changes {
          background: var(--color-bg-tertiary);
          border-radius: var(--radius-md);
          padding: var(--space-3);
          margin-bottom: var(--space-3);
        }

        .changes-title {
          font-size: var(--font-size-xs);
          color: var(--color-text-tertiary);
          text-transform: uppercase;
          margin-bottom: var(--space-2);
        }

        .change-item {
          font-size: var(--font-size-sm);
          margin-bottom: var(--space-1);
        }

        .change-key {
          color: var(--color-text-secondary);
        }

        .change-value {
          color: var(--color-text-primary);
          font-weight: 500;
        }

        .variant-expected {
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
        }

        .expected-label {
          color: var(--color-text-tertiary);
        }

        .test-config-summary {
          background: var(--color-bg-secondary);
          border-radius: var(--radius-md);
          padding: var(--space-4);
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: var(--space-4);
        }

        .config-item {
          text-align: center;
        }

        .config-label {
          display: block;
          font-size: var(--font-size-xs);
          color: var(--color-text-tertiary);
          margin-bottom: var(--space-1);
        }

        .config-value {
          font-weight: 600;
          color: var(--color-text-primary);
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: var(--space-2);
          padding: var(--space-3) var(--space-5);
          border-radius: var(--radius-md);
          font-weight: 500;
          cursor: pointer;
          transition: all var(--transition-fast);
        }

        .btn-primary {
          background: var(--color-primary);
          color: white;
          border: none;
        }

        .btn-primary:hover:not(:disabled) {
          background: var(--color-primary-dark);
        }

        .btn-primary:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-secondary {
          background: var(--color-bg-secondary);
          color: var(--color-text-primary);
          border: 1px solid var(--color-border);
        }

        .btn-secondary:hover {
          background: var(--color-bg-tertiary);
        }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        .animate-pulse {
          animation: pulse 2s ease-in-out infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default VariantLab;
